import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingCart, Store, Truck, ArrowRight } from "lucide-react";
import { useCart, lineTotals } from "@/context/CartContext";
import { fmtMoney, fileUrl } from "@/lib/api";

export default function CartDrawer() {
  const { items, updateQty, removeItem, subtotal, savings, count, drawerOpen, setDrawerOpen } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const go = (path) => {
    setDrawerOpen(false);
    navigate(path);
  };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[60] bg-[#050B1E]/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
            data-testid="cart-drawer-overlay"
          />
          <motion.aside
            className="fixed top-0 right-0 bottom-0 z-[61] w-full max-w-[400px] bg-white flex flex-col shadow-2xl"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-5 py-4 bg-[#0A1C40] text-white">
              <div className="font-display font-black text-lg flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-[#FFC220]" /> Your Cart
                {count > 0 && <span className="text-[#FFC220] text-sm">({count})</span>}
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                data-testid="cart-drawer-close"
                aria-label="Close cart"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-testid="cart-drawer-empty">
                <ShoppingCart className="w-10 h-10 text-gray-300" />
                <div className="font-display font-bold text-lg mt-4">Your cart is empty</div>
                <p className="text-sm text-gray-500 mt-1">Real inventory, wholesale prices — go find a deal.</p>
                <button onClick={() => go("/shop")} className="btn-primary mt-6 text-sm" data-testid="cart-drawer-shop-btn">
                  Shop the Floor
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                  {items.map((i) => {
                    const lt = lineTotals(i);
                    return (
                      <div key={i.key} className="flex gap-3" data-testid={`drawer-item-${i.key}`}>
                        {i.image ? (
                          <img src={fileUrl(i.image)} alt={i.name} className="w-16 h-16 object-cover border border-gray-200 shrink-0" />
                        ) : (
                          <div className="w-16 h-16 bg-[#0A1C40] text-[#FFC220] text-[9px] font-black flex items-center justify-center text-center p-1 shrink-0">PALLET</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold leading-snug line-clamp-2">{i.name}</div>
                          {i.color && <div className="text-xs text-gray-400">Color: {i.color}</div>}
                          <div className="flex items-center justify-between mt-1.5">
                            <div className="flex items-center gap-1">
                              <button onClick={() => updateQty(i.key, i.quantity - 1)} className="p-1 border border-gray-300 hover:border-[#0A1C40]" data-testid={`drawer-qty-minus-${i.key}`}>
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="w-7 text-center text-sm font-bold">{i.quantity}</span>
                              <button onClick={() => updateQty(i.key, Math.min(i.quantity + 1, i.stock))} className="p-1 border border-gray-300 hover:border-[#0A1C40]" data-testid={`drawer-qty-plus-${i.key}`}>
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-sm">{fmtMoney(lt.total)}</div>
                              {lt.savings > 0 && <div className="text-[10px] text-green-600 font-bold">deal −{fmtMoney(lt.savings)}</div>}
                            </div>
                          </div>
                        </div>
                        <button onClick={() => removeItem(i.key)} className="text-gray-300 hover:text-red-600 self-start transition-colors" data-testid={`drawer-remove-${i.key}`} aria-label="Remove item">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t-2 border-[#0A1C40] px-5 py-4 bg-gray-50 [padding-bottom:max(1rem,env(safe-area-inset-bottom))]">
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-semibold text-gray-600 mb-3">
                    <span className="inline-flex items-center gap-1"><Store className="w-3.5 h-3.5 text-[#0A1C40]" /> Pickup in store</span>
                    <span className="inline-flex items-center gap-1"><Truck className="w-3.5 h-3.5 text-[#0A1C40]" /> FREE local delivery $299+</span>
                  </div>
                  {savings > 0 && (
                    <div className="flex justify-between text-sm text-green-700 font-bold">
                      <span>Quantity deal savings</span><span>−{fmtMoney(savings)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline">
                    <span className="font-bold">Subtotal</span>
                    <span className="font-display font-black text-xl" data-testid="drawer-subtotal">{fmtMoney(subtotal)}</span>
                  </div>
                  <div className="text-[11px] text-gray-400">Tax & any delivery calculated at checkout</div>
                  <button onClick={() => go("/checkout")} className="btn-gold w-full mt-3 flex items-center justify-center gap-2 !py-3" data-testid="drawer-checkout-btn">
                    Checkout <ArrowRight className="w-4 h-4" />
                  </button>
                  <button onClick={() => go("/cart")} className="w-full mt-2 text-sm font-bold text-[#0A1C40] underline underline-offset-2" data-testid="drawer-view-cart-btn">
                    View full cart
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
